/**
 * Optional third-party analytics integration.
 *
 * Google Analytics and Plausible load only when their deployment settings are
 * explicitly configured. StageCrew's privacy-minimal first-party product
 * events are recorded separately and do not depend on either provider.
 *
 * An operator setting is not visitor consent. Do not enable either provider
 * without an accurate privacy notice and an appropriate consent/legal-basis
 * decision for the audiences StageCrew serves.
 */

let gaInitialized = false;
let plausibleInitialized = false;
let plausibleDomain: string | undefined;

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
    plausible: ((event: string, options?: { props?: Record<string, string | number>; u?: string }) => void) & { q?: unknown[][] };
  }
}

// --- Google Analytics 4 ---

export function initGA(
  measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID,
): void {
  const gaId = measurementId?.trim();
  if (!gaId || gaInitialized) return;

  if (!document.querySelector(`script[src*="${encodeURIComponent(gaId)}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', gaId, { send_page_view: false });

  gaInitialized = true;
}

// --- Plausible Analytics ---

export function initPlausible(
  domain = process.env.REACT_APP_PLAUSIBLE_DOMAIN,
): void {
  const configuredDomain = domain?.trim();
  if (!configuredDomain || plausibleInitialized) return;

  // Load Plausible script (manual mode for SPA control)
  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = configuredDomain;
  script.dataset.api = 'https://plausible.io/api/event';
  script.src = 'https://plausible.io/js/script.manual.js';
  document.head.appendChild(script);

  // Initialize plausible queue before script loads
  window.plausible = window.plausible || function plausibleStub(...args: unknown[]) {
    (window.plausible.q = window.plausible.q || []).push(args);
  } as typeof window.plausible;

  plausibleDomain = configuredDomain;
  plausibleInitialized = true;
}

// --- Unified tracking ---

export function sanitizeAnalyticsPath(path: string): string {
  try {
    return new URL(path, window.location.origin).pathname || '/';
  } catch {
    return path.split(/[?#]/, 1)[0] || '/';
  }
}

export function trackPageView(path: string): void {
  const safePath = sanitizeAnalyticsPath(path);
  if (gaInitialized && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: safePath,
      page_location: window.location.origin + safePath,
    });
  }
  if (plausibleDomain && plausibleInitialized) {
    window.plausible('pageview', { u: window.location.origin + safePath });
  }
}

export function trackEvent(action: string, category: string, _label?: string, value?: number): void {
  if (gaInitialized && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      value,
    });
  }
  if (plausibleDomain && plausibleInitialized) {
    window.plausible(action, {
      props: {
        category,
        ...(value !== undefined ? { value } : {}),
      },
    });
  }
}
